import React, { useEffect } from 'react';
import { Button, Link, cn } from '@jem-open/jem-ui';
import moment from 'moment';
import { ElasticMessage } from '../../../../types/MessageTypes';

interface WhatsAppMessageProps {
    message: ElasticMessage
};

export default function WhatsAppMessage({message}: WhatsAppMessageProps) {

  const [messageBody, setMessageBody] = React.useState<string>('');

  const escapeHtml = (text:string) => text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const formatWhatsappMessage = (text:string) => escapeHtml(text)
    .replace(/(?:\*)(?:(?!\s))((?:(?!\*|\n).)+)(?:\*)/g,'<b>$1</b>')
    .replace(/(?:_)(?:(?!\s))((?:(?!\n|_).)+)(?:_)/g,'<i>$1</i>')
    .replace(/(?:~)(?:(?!\s))((?:(?!\n|~).)+)(?:~)/g,'<s>$1</s>')
    .replace(/(?:--)(?:(?!\s))((?:(?!\n|--).)+)(?:--)/g,'<u>$1</u>')
    .replace(/(?:```)(?:(?!\s))((?:(?!\n|```).)+)(?:```)/g,'<tt>$1</tt>')
    .replace(/\n/g, '<br />');

  useEffect(() => {
      let body = 'Message Empty';

      switch (message.type) {
        case 'text':
          if (typeof message.message.body === 'string') {
            body = message.message.body;
          } else {
            body = message.message.body?.text ||  message.message.text?.body || '';
          }
          break;
        case 'interactive':
          if (message.message.type === "list" && typeof message.message.body === 'object') {
            body = message.message.body?.text || '';
          }
          if (message.message.type === "list_reply") {
            body = message.message?.list_reply?.title || '';
          }
          if (message.message.type === "button_reply"){
            body = message.message?.button_reply?.title || '';
          }
          if (message.message.type === "button"  && typeof message.message.body === 'object') {
            body = message.message.body?.text || '';
          }
          break;
        case 'template':
            body = `Template: *${message.message.template?.name || ''}*\n`;
            if (message.message.template?.components?.length) {
              body += '\n*Components:*\n';
              body += message.message.template?.components?.map((component) => {
                if (component.type === 'body') {
                  return component.parameters?.map((parameter) => `${parameter.type}: ${parameter.text || ''}`).join('\n');
                }
                if (component.type === 'button') {
                  return component.parameters?.map((parameter) => `${parameter.type}: ${parameter.payload || ''}`).join('\n');
                }
                return '';
              }).join('\n') || '';
            }
          break;
        default:
          break;
      }
      setMessageBody(formatWhatsappMessage(body));
  }, [message]);

  const isReply =
    message.message?.type === 'list_reply' ||
    message.message?.type === 'button_reply';
  const isOutgoing = Boolean(message.sender_profile?.company_name);

  const renderMessage = () => {
    if (isReply) {
      return (
        <Button size="sm" variant="outline" className="w-full" disabled>
          {messageBody}
        </Button>
      );
    }
    return (
      // eslint-disable-next-line react/no-danger
      <div dangerouslySetInnerHTML={{ __html: messageBody }} />
    );
  };

  return (
    <div
      className={cn(
        'flex w-full flex-col',
        isOutgoing ? 'items-end' : 'items-start'
      )}>
      <div className="w-full max-w-[400px]">
        <div
          className={cn(
            'rounded-xl text-sm text-greyscale-text-body',
            !isReply && 'border border-greyscale-border-default p-sm shadow-sm',
            !isReply &&
              (isOutgoing
                ? 'bg-secondary-surface-subtle'
                : 'bg-primary-surface-subtle')
          )}>
          {message.message?.header?.type &&
            (message.message?.header?.type === 'text' ||
              message.message?.header?.type === 'image') && (
              <div className="mb-xs border-b border-greyscale-border-default pb-xs">
                {message.message?.header?.type === 'image' && (
                  <img
                    src={message.message.header.image?.link}
                    alt="header"
                    className="h-auto w-full rounded-lg"
                  />
                )}
                {message.message?.header?.type === 'text' && (
                  <h5 className="m-none text-left font-semibold text-greyscale-text-title">
                    {message.message.header?.text}
                  </h5>
                )}
              </div>
            )}

          <div className="flex justify-between break-words">
            {renderMessage()}
          </div>

          {message.message?.action?.sections?.map((section) => (
            <div key={section.title} className="mt-md">
              <h3 className="text-center text-sm font-semibold text-greyscale-text-title">
                {section.title}
              </h3>
              {section.rows.map((row) => (
                <Button
                  key={row.id}
                  size="sm"
                  variant="outline"
                  className="mt-xs w-full"
                  disabled>
                  {row.title}
                </Button>
              ))}
            </div>
          ))}

          {message.message?.action?.buttons?.map((button) => (
            <div key={button.reply.id} className="mt-md">
              <Button size="sm" variant="outline" className="w-full" disabled>
                {button.reply.title}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-xxs flex justify-between text-xs text-greyscale-text-caption">
          <div>
            {message.sender_profile?.company_name ||
              message.sender_profile?.mobile_number}
            {message.sender_profile?.first_name && (
              <>
                <br />
                {message.sender_profile?.first_name}{' '}
                <Link
                  href={`/admin/employees/${message.sender_profile?.id}`}
                  size="xs">
                  #{message.sender_profile?.id}
                </Link>
              </>
            )}
          </div>
          <div>{moment.unix(message.timestamp).format('DD/MM/YYYY HH:mm')}</div>
        </div>
      </div>
    </div>
  );
}
